import {
  useEffect,
  useState,
  memo,
  useMemo
} from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Card,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Typography,
  useTheme,
  useMediaQuery,
  Grid,
  Divider,
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import {
  getReconciliationList,
} from 'src/redux/reportSlice';
import Loader from 'src/common/Loader';
import EnhancedTableHead, { getComparator } from 'src/common/EnhancedTableHead';
import { getAlignByColumnId, reconciliationReportTableHeaders } from 'src/utils/tableHeaders';
import {
  getFormattedDateOnly,
  getFuelQuantity,
  getReconciliationQueryParams,
} from 'src/utils/getInitials';
import CustomPagination from 'src/common/CustomPagination';

/* ================= MOBILE VIEW ================= */

const MobileCardView = memo(({ item }) => (
  <Card
    sx={{
      mb: 2,
      p: 2,
      borderRadius: 2,
      border: '1px solid #E5E7EB',
      boxShadow: '0 1px 3px rgba(0,0,0,0.08)'
    }}
  >
    <Typography variant="subtitle2" fontWeight={700}>
      ID:
      {' '}
      {item.id}
    </Typography>

    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
      {item.station_name || 'N/A'}
    </Typography>

    <Divider sx={{ my: 1 }} />

    <Grid container spacing={1}>
      <Grid item xs={6}>
        <Typography variant="caption">Site</Typography>
        <Typography variant="body2">
          {item.site_name || 'N/A'}
        </Typography>
      </Grid>

      <Grid item xs={6}>
        <Typography variant="caption">Date</Typography>
        <Typography variant="body2">
          {item.date
            ? getFormattedDateOnly(item.date, 'DD/MM/YYYY')
            : 'N/A'}
        </Typography>
      </Grid>

      <Grid item xs={6}>
        <Typography variant="caption">Opening</Typography>
        <Typography variant="body2">
          {getFuelQuantity(item.opening_value, item.opening_value_gallons) || '0 L'}
        </Typography>
      </Grid>

      <Grid item xs={6}>
        <Typography variant="caption">Inward</Typography>
        <Typography variant="body2">
          {getFuelQuantity(item.inward, item.inward_gallons) || '0 L'}
        </Typography>
      </Grid>

      <Grid item xs={6}>
        <Typography variant="caption">Transfer</Typography>
        <Typography variant="body2">
          {getFuelQuantity(item.transfer, item.transfer_gallons) || '0 L'}
        </Typography>
      </Grid>

      <Grid item xs={6}>
        <Typography variant="caption">Sales</Typography>
        <Typography variant="body2">
          {getFuelQuantity(item.sales, item.sales_gallons) || '0 L'}
        </Typography>
      </Grid>

      <Grid item xs={12}>
        <Typography variant="caption">Closing Stock</Typography>
        <Typography variant="body2" fontWeight={600}>
          {getFuelQuantity(item.stock, item.stock_gallons) || '0 L'}
        </Typography>
      </Grid>
    </Grid>
  </Card>
));

MobileCardView.propTypes = {
  item: PropTypes.object.isRequired
};

/* ================= MAIN TABLE ================= */

const ReconciliationReportTable = ({
  refreshData,
  searchText,
  siteFilter,
  dispenserFilter,
  dateFilter,
}) => {
  const theme = useTheme();
  const dispatch = useDispatch();

  const { loading, reconciliationList, totalCount } = useSelector((state) => state.reports);

  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [limit, setLimit] = useState(10);
  const [page, setPage] = useState(0);
  const [order, setOrder] = useState('desc');
  const [orderBy, setOrderBy] = useState('date');
  const reconciliationAlignById = useMemo(
    () => getAlignByColumnId(reconciliationReportTableHeaders),
    []
  );
  const reconciliationColumnCount = reconciliationReportTableHeaders.length;
  const reconciliationParams = useMemo(
    () => getReconciliationQueryParams(
      searchText,
      siteFilter,
      dispenserFilter,
      dateFilter
    ),
    [dateFilter, dispenserFilter, searchText, siteFilter]
  );

  useEffect(() => {
    setPage(0);
    dispatch(
      getReconciliationList({
        limit,
        pageNo: 1,
        params: reconciliationParams,
      })
    );
  }, [dispatch, limit, reconciliationParams, refreshData]);

  const handleLimitChange = (event) => {
    const newLimit = parseInt(event.target.value, 10);
    setPage(0);
    setLimit(newLimit);
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
    dispatch(
      getReconciliationList({
        limit,
        pageNo: newPage + 1,
        params: reconciliationParams,
      })
    );
  };

  const handleRequestSort = (e, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const sortedData = reconciliationList?.slice().sort(getComparator(order, orderBy)) || [];
  const displayData = sortedData;

  if (loading) return <Loader />;

  /* ================= MOBILE ================= */

  if (isMobile) {
    return (
      <>
        {displayData.length > 0 ? (
          displayData.map((item) => (
            <MobileCardView key={item.id} item={item} />
          ))
        ) : (
          <Card sx={{ p: 3, textAlign: 'center' }}>
            <Typography>No reconciliation records found</Typography>
          </Card>
        )}

        <CustomPagination
          page={page}
          rowsPerPage={limit}
          count={totalCount}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleLimitChange}
        />
      </>
    );
  }

  /* ================= DESKTOP ================= */

  return (
    <Box
      sx={{
        bgcolor: '#FFFFFF',
        borderRadius: 2,
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden'
      }}
    >
      <Box
        sx={{
          minHeight: 0,
          flex: 1,
          overflow: 'auto',
          '& .MuiTableCell-root': {
            whiteSpace: 'nowrap'
          },
          '& .MuiTableCell-stickyHeader': {
            backgroundColor: '#F9FAFB',
            borderBottom: `1px solid ${theme.palette.divider}`,
            zIndex: 2
          }
        }}
      >
        <Box sx={{ minWidth: { xs: 900, sm: 1040, md: 1180 } }}>
          <Table stickyHeader>
            <EnhancedTableHead
              order={order}
              orderBy={orderBy}
              headCells={reconciliationReportTableHeaders}
              onRequestSort={handleRequestSort}
              sx={{
                backgroundColor: '#F9FAFB',
                borderBottom: `1px solid ${theme.palette.divider}`
              }}
            />

            <TableBody>
              {displayData.length > 0 ? (
                displayData.map((item) => (
                  <TableRow
                    hover
                    key={item.id}
                    sx={{
                      borderBottom: `1px solid ${theme.palette.divider}`,
                      '&:hover': {
                        backgroundColor: '#F9FAFB'
                      }
                    }}
                  >
                    <TableCell align={reconciliationAlignById.id}>{item.id}</TableCell>
                    <TableCell align={reconciliationAlignById.station_name}>{item.station_name || 'N/A'}</TableCell>
                    <TableCell align={reconciliationAlignById.site_name}>{item.site_name || 'N/A'}</TableCell>
                    <TableCell align={reconciliationAlignById.date}>
                      {item.date
                        ? getFormattedDateOnly(item.date, 'DD/MM/YYYY')
                        : 'N/A'}
                    </TableCell>
                    <TableCell align={reconciliationAlignById.opening_value}>
                      {getFuelQuantity(item.opening_value, item.opening_value_gallons) || '0 L'}
                    </TableCell>
                    <TableCell align={reconciliationAlignById.inward}>
                      {getFuelQuantity(item.inward, item.inward_gallons) || '0 L'}
                    </TableCell>
                    <TableCell align={reconciliationAlignById.transfer}>
                      {getFuelQuantity(item.transfer, item.transfer_gallons) || '0 L'}
                    </TableCell>
                    <TableCell align={reconciliationAlignById.sales}>
                      {getFuelQuantity(item.sales, item.sales_gallons) || '0 L'}
                    </TableCell>
                    <TableCell align={reconciliationAlignById.stock}>
                      {getFuelQuantity(item.stock, item.stock_gallons) || '0 L'}
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={reconciliationColumnCount} align="center" sx={{ py: 6 }}>
                    <Typography color="text.secondary">
                      No reconciliation records found
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Box>
      </Box>

      <Divider sx={{ flexShrink: 0 }} />

      <Box sx={{ p: 1, flexShrink: 0, bgcolor: '#FFFFFF' }}>
        <CustomPagination
          page={page}
          rowsPerPage={limit}
          count={totalCount}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleLimitChange}
        />
      </Box>
    </Box>
  );
};

ReconciliationReportTable.propTypes = {
  refreshData: PropTypes.bool,
  searchText: PropTypes.string,
  dateFilter: PropTypes.string,
  dispenserFilter: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number
  ]),
  siteFilter: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number
  ]),
};

export default ReconciliationReportTable;
